/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import * as React from 'react';

import type { PropsOf } from '../propsOf';
import { TextInput, type TextInputFeedback } from './TextInput';

export interface DebouncedTextInputOwnProps<E extends React.ElementType = typeof TextInput> {
    /**
     * Initial value to use for the input.
     */
    value: string;

    /**
     * Callback when the value is changed. It's usually called when the input is blurred.
     * Throwing an error in this callback will reset it to the initial value.
     */
    onChangeText: (value: string) => void | Promise<void>;

    /**
     * Callback when the value is changed. It does not wait for the input to be blurred.
     */
    onChange?: (value: string) => void;

    normalize?: (value: string) => string;
    validate?: (value: string) => TextInputFeedback | undefined;
    component?: E;
}

export type DebouncedTextInputProps<E extends React.ElementType = typeof TextInput> =
    DebouncedTextInputOwnProps<E> & Omit<PropsOf<E>, keyof DebouncedTextInputOwnProps>;

/*
 * TextInput that only triggers onChange when blurred.
 */
export function DebouncedTextInput<E extends React.ElementType = typeof TextInput>({
    ref,
    ...props
}: DebouncedTextInputProps<E> & {
    ref?: React.ComponentPropsWithRef<E>['ref'];
}): React.ReactElement {
    const {
        component: C = TextInput,
        value: initialValue,
        onChangeText,
        onChange,
        normalize,
        validate,
        ...rest
    } = props;
    const Component = C as React.ElementType;
    const [value, setValue] = React.useState<string | undefined>(undefined);
    const valueRef = React.useRef(value);
    valueRef.current = value;

    React.useEffect(() => {
        if (typeof valueRef.current !== 'undefined') {
            setValue(undefined);
        }
    }, [valueRef, initialValue]);

    const onBlur = async () => {
        if (typeof value === 'string') {
            try {
                await onChangeText(value);
            } catch (_err) {
                // When an error occurs in onChangeText, we go back to the original value
                setValue(initialValue);
            }
        }
    };

    const currentValue = value === undefined ? initialValue : value;

    return (
        <Component
            ref={ref}
            {...rest}
            value={currentValue}
            feedback={validate ? validate(currentValue) : undefined}
            onChangeText={(newText) => {
                const normalizedText = normalize ? normalize(newText) : newText;
                onChange?.(normalizedText);
                setValue(normalizedText);
            }}
            onBlur={onBlur}
        />
    );
}
